/**
 * Where we are: the landing page (`#/` or no hash), the app (`#/app`: the projects dashboard, or the editor once a
 * project is open) or the manual (`#/help`, `#/help/timeline`, `#/help/timeline:markers` for a part of a page). A hash
 * route, so the whole thing is one static page with no server routes.
 *
 * Inside the app the address says what's open, so a refresh (or a link kept) comes back to it: `#/app/p/<project>` the
 * editor on a project, `…/thumbnail` its thumbnail studio, `…/music` Make music over it, and `#/app/music` Make music
 * from the projects page.
 */
import { create } from 'zustand';
import { helpHref } from './help/href';

export { helpHref };

export type Page = 'landing' | 'app' | 'help';
const pageOf = (hash: string): Page =>
  hash.startsWith('#/app') ? 'app' : hash.startsWith('#/help') ? 'help' : 'landing';

/** What's open in the app: a project (its id) and, over it, the thumbnail studio or Make music. */
export interface AppRoute {
  project: string | null;
  view: 'thumbnail' | 'music' | null;
}
const VIEWS = ['thumbnail', 'music'] as const;
export function routeOf(hash: string): AppRoute {
  const parts = hash
    .replace(/^#\/app\/?/, '')
    .split('/')
    .filter(Boolean)
    .map(decodeURIComponent);
  const view = (v?: string) => ((VIEWS as readonly string[]).includes(v ?? '') ? (v as AppRoute['view']) : null);
  if (parts[0] === 'p' && parts[1]) return { project: parts[1], view: view(parts[2]) };
  return { project: null, view: parts[0] === 'music' ? 'music' : null };
}
export const hashOf = (r: AppRoute) =>
  r.project
    ? `#/app/p/${encodeURIComponent(r.project)}${r.view ? `/${r.view}` : ''}`
    : r.view === 'music'
      ? '#/app/music'
      : '#/app';

export const useNav = create<{ page: Page; route: AppRoute }>(() => ({
  page: pageOf(location.hash),
  route: routeOf(location.hash),
}));

/**
 * Say in the address what's open in the app (a new step back in the browser's history, or in place of this one), so a
 * refresh comes back to it. Parts left out stay as they are.
 */
export function setRoute(part: Partial<AppRoute>, o: { replace?: boolean } = {}) {
  const r = { ...useNav.getState().route, ...part };
  const hash = hashOf(r);
  if (location.hash !== hash) history[o.replace ? 'replaceState' : 'pushState'](null, '', hash);
  useNav.setState({ page: 'app', route: r });
}

/** The manual in a tab of its own, so the editor stays as it is. */
export function openHelp(doc?: string, part?: string) {
  window.open(`${location.pathname}${helpHref(doc, part)}`, 'cutroom-help');
}

export function go(page: Page) {
  const hash = page === 'app' ? hashOf(useNav.getState().route) : page === 'help' ? '#/help' : '#/';
  if (location.hash !== hash) history.pushState(null, '', hash);
  useNav.setState({ page });
  window.scrollTo(0, 0);
}

const follow = () => useNav.setState({ page: pageOf(location.hash), route: routeOf(location.hash) });
window.addEventListener('popstate', follow);
window.addEventListener('hashchange', follow);
